import React, { useState, useEffect } from 'react';
import { Form } from 'antd';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../../../contexts/ToastContext';
import { apiService } from '../../../services/api';
import { menteeProfileQueryKeys } from '../../../lib/queryClient';
import { MenteeProfileResponse, UpdateMenteeProfileRequest, CategoryResponse } from '../../../types';
import ProfileForm from '../../../components/Mentee/ProfileForm';
import PanelTabCard from '../../../components/Common/PanelTabCard';
import { useAuth } from '../../../contexts/AuthContext';

const ProfileTab: React.FC = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { refreshUser } = useAuth();
  const [form] = Form.useForm();

  const { data: profileData, isLoading: loading } = useQuery({
    queryKey: menteeProfileQueryKeys.profile,
    queryFn: async () => {
      const res = await apiService.getMenteeProfile();
      if (!res.success) throw new Error(res.message);
      return res.data;
    },
  });

  const { data: categories = [] } = useQuery({
    queryKey: menteeProfileQueryKeys.categories,
    queryFn: async () => {
      const res = await apiService.getCategories();
      if (!res.success) throw new Error(res.message);
      return res.data;
    },
  });

  useEffect(() => {
    if (profileData) {
      form.setFieldsValue({
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        phoneNumber: profileData.phoneNumber,
        websiteUrl: profileData.websiteUrl,
        city: profileData.city,
        country: profileData.country,
        bio: profileData.bio,
        linkedInProfile: profileData.linkedInProfile,
        githubProfile: profileData.githubProfile,
        twitterProfile: profileData.twitterProfile
      });
    }
  }, [profileData, form]);

  const updateMutation = useMutation({
    mutationFn: (data: UpdateMenteeProfileRequest) => apiService.updateMenteeProfile(data),
    onSuccess: async (res) => {
      if (res.success) {
        toast.success('Profil başarıyla güncellendi!');
        queryClient.setQueryData(menteeProfileQueryKeys.profile, res.data);
        await refreshUser();
      } else toast.error(res.message);
    },
    onError: () => toast.error('Profil güncellenirken hata oluştu.'),
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => apiService.uploadMenteeProfileImage(file),
    onSuccess: async (res) => {
      if (res.success) {
        toast.success('Profil fotoğrafı başarıyla güncellendi!');
        queryClient.invalidateQueries({ queryKey: menteeProfileQueryKeys.profile });
        await refreshUser();
      } else toast.error(res.message);
    },
    onError: () => toast.error('Fotoğraf yüklenirken hata oluştu.'),
  });

  const handleSave = (values: any) => {
    const updateData: UpdateMenteeProfileRequest = {
      firstName: values.firstName,
      lastName: values.lastName,
      phoneNumber: values.phoneNumber,
      websiteUrl: values.websiteUrl,
      city: values.city,
      country: values.country,
      bio: values.bio,
      linkedInProfile: values.linkedInProfile,
      githubProfile: values.githubProfile,
      twitterProfile: values.twitterProfile
    };
    updateMutation.mutate(updateData);
  };

  const handleImageUpload = (file: File) => {
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Dosya boyutu 10MB\'dan büyük olamaz.');
      return false;
    }
    uploadMutation.mutate(file);
    return false;
  };

  return (
    <PanelTabCard
      title="Profil Bilgileri"
      description="Profil bilgilerinizi güncelleyin ve diğer kullanıcılara nasıl görüneceğinizi belirleyin."
    >
      <ProfileForm
        form={form}
        profileData={profileData ?? null}
        categories={categories}
        loading={loading}
        saving={updateMutation.isPending}
        uploading={uploadMutation.isPending}
        onSave={handleSave}
        onImageUpload={handleImageUpload}
      />
    </PanelTabCard>
  );
};

export default ProfileTab;
